import { useSearchParams } from "react-router-dom";
import {
  Card,
  CardHeader,
  CardBody,
  MetricCard,
  InsightCard,
  DataTable,
  Chart,
  ChartLegend,
  DashboardSkeleton,
  PageError,
  SampleDataBanner,
} from "../components/index.js";
import { useDashboardData } from "../hooks/useDashboardData.js";
import { rangeDays } from "../lib/time.js";

/**
 * Overview page — fetches from GET /api/dashboard/overview.
 * Shape matches CLAUDE.md contract; see packages/api/src/services/dashboard.js.
 */
export function Overview() {
  const { data, loading, error, refetch } = useDashboardData("overview");
  const [searchParams] = useSearchParams();
  const days = rangeDays(searchParams.get("from"), searchParams.get("to"));

  if (loading) return <DashboardSkeleton />;
  if (error)   return <PageError error={error} onRetry={refetch} />;
  if (!data)   return null;

  const { metrics, dailyTraffic, trafficSources, devices, insights, period, clientContext } = data;
  const isEcommerce = clientContext?.isEcommerce;

  const dailySeries = [
    { key: "direct",  color: "#ff6804", label: "Direct" },
    { key: "organic", color: "#a3a3a3", label: "Google Organic" },
    { key: "cpc",     color: "#d4d4d4", label: "Google Ads" },
    { key: "other",   color: "#e5e5e5", label: "Other" },
  ];

  return (
    <div className="space-y-5 max-w-[1400px]">
      {period?.hasRealData === false && <SampleDataBanner />}
      {/*
        Third card swaps by business type:
          service biz → Form Submissions (their conversion vehicle)
          ecommerce   → Abandoned Carts (a real recoverable number — generate_lead
                        events on Shopify stores are usually search-bar / contact-form
                        noise, which is why we don't show them here).
      */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard {...metrics.sessions} />
        <MetricCard {...metrics.onlineOrders} />
        {isEcommerce
          ? <MetricCard {...metrics.abandonedCarts} />
          : <MetricCard {...metrics.formSubmissions} />}
        <MetricCard {...metrics.adSpend} />
      </div>

      <Card>
        <CardHeader
          title="Daily Traffic"
          subtitle={`Sessions by source · last ${days} days`}
          right={
            <ChartLegend
              items={dailySeries.map((s) => ({ label: s.label, color: s.color }))}
            />
          }
        />
        <CardBody>
          <Chart
            type="line"
            data={dailyTraffic}
            xKey="date"
            series={dailySeries}
            height={220}
          />
        </CardBody>
      </Card>

      {insights?.[0] && <InsightCard>{insights[0].content}</InsightCard>}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2">
          <CardHeader
            title="Traffic Sources"
            subtitle={`Last ${days} days — sessions by channel`}
          />
          <DataTable
            columns={[
              { key: "source",   label: "Source" },
              { key: "sessions", label: "Sessions", align: "right",
                render: (r) => r.sessions.toLocaleString() },
              { key: "pct",      label: "Share",    align: "right",
                render: (r) => `${r.pct}%` },
              { key: "note",     label: "Note" },
            ]}
            rows={trafficSources}
            highlightFn={(r) => r.source === "ChatGPT / AI"}
          />
        </Card>

        <div className="space-y-4">
          <Card>
            <CardHeader title="Devices" subtitle="Active users" />
            <CardBody>
              <div className="space-y-2.5">
                {devices.map((d) => (
                  <div key={d.name}>
                    <div className="flex justify-between items-baseline mb-1">
                      <span className="text-[12px] text-text-primary">{d.name}</span>
                      <span className="text-[12px] text-text-secondary">
                        {d.value.toLocaleString()}{" "}
                        <span className="text-text-muted">· {d.pct}%</span>
                      </span>
                    </div>
                    <div className="h-1.5 bg-border-light rounded-full overflow-hidden">
                      <div
                        className="h-full bg-accent"
                        style={{ width: `${d.pct}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </CardBody>
          </Card>

          {insights?.[1] && <InsightCard>{insights[1].content}</InsightCard>}
        </div>
      </div>
    </div>
  );
}
